// Librairies
import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject } from 'rxjs';

// Services
import { Pop } from 'src/app/classes/shared/pop';
import { CompanyService } from 'src/app/services/api/companies/company.service';
import { CompanyStore } from 'src/app/services/stores/company.store';

// Interfaces
import { CompanyDetail, CompanySummary } from 'src/app/interfaces/api/company';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { CompanyLogoComponent } from 'src/app/components/shared/company-logo/company-logo.component';
import { ButtonComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { CompanyDrawerComponent } from './company-drawer/company-drawer.component';

@Component({
  selector: 'app-companies',
  standalone: true,
  imports: [
    MatIconModule, MatPaginatorModule, MatTooltipModule, TranslateModule,
    ButtonComponent, SearchBarComponent, SpinnerComponent, NoDataComponent, CompanyLogoComponent, CompanyDrawerComponent
  ],
  templateUrl: './companies.component.html',
  styleUrl: './companies.component.scss'
})

export class CompaniesComponent {
  // Services
  private readonly companyService = inject(CompanyService);
  private readonly companyStore = inject(CompanyStore);
  private readonly pop = inject(Pop);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  // Signals
  readonly companies = signal<CompanySummary[]>([]);
  readonly total = signal(0);
  readonly loading = signal(true);
  readonly failed = signal(false);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(20);
  readonly expandedId = signal<string | null>(null);
  readonly details = signal<Record<string, CompanyDetail>>({});
  // null: closed; { id: null }: creation.
  readonly drawer = signal<{ id: string | null } | null>(null);
  readonly currentId = computed(() => this.companyStore.current()?.id);

  // Variables
  query = '';
  private readonly search$ = new Subject<string>();

  constructor() {
    this.search$.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(query => {
      this.query = query;
      this.pageIndex.set(0);
      this.load();
    });
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.failed.set(false);
    this.companyService.searchAndCountCompanies({
      q: this.query,
      offset: this.pageIndex() * this.pageSize(),
      limit: this.pageSize()
    }).subscribe({
      next: ({ results, total }) => {
        this.companies.set(results);
        this.total.set(total);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.failed.set(true);
      }
    });
  }

  search(query: string): void {
    this.search$.next(query.trim());
  }

  onPage({ pageIndex, pageSize }: PageEvent): void {
    this.pageIndex.set(pageIndex);
    this.pageSize.set(pageSize);
    this.load();
  }

  toggle(company: CompanySummary): void {
    const expand = this.expandedId() !== company.id;
    this.expandedId.set(expand ? company.id : null);
    if (expand && !this.details()[company.id]) this.loadDetail(company.id);
  }

  onSaved(): void {
    this.details.set({});
    const expanded = this.expandedId();
    if (expanded) this.loadDetail(expanded);
    this.load();
  }

  activate(company: CompanySummary): void {
    this.companyStore.select(company);
    this.pop.success(this.translate.instant('companies.activated', { name: company.name }));
  }

  openTenants(company: CompanySummary): void {
    this.router.navigate(['/company', company.id]);
  }

  remove(company: CompanySummary): void {
    this.pop.confirm(this.translate.instant('companies.delete_confirm', { name: company.name }), (confirm: { ok: boolean }) => {
      if (!confirm.ok) return;
      this.companyService.removeCompany(company.id).subscribe({
        next: () => {
          this.pop.success(this.translate.instant('companies.deleted'));
          this.companyStore.forget(company.id);
          this.load();
        },
        error: err => this.pop.error(this.translate.instant(err?.status === 409 ? 'companies.errors.not_empty' : 'companies.errors.generic'))
      });
    }, 'form.delete');
  }

  private loadDetail(id: string): void {
    this.companyService.getCompany(id).subscribe(detail => this.details.update(details => ({ ...details, [id]: detail })));
  }
}
